import { create } from 'zustand';

/* ------------------------------------------------------------------ */
/*  Types matching the backend ConnectionStatus / ConnectionStatusSummary  */
/* ------------------------------------------------------------------ */

/** Maps to C# `ConnectionType` enum values */
export const ConnectionType = {
  PostgreSQL: 0,
  Neo4j: 1,
  Qdrant: 2,
} as const;

export type ConnectionType = (typeof ConnectionType)[keyof typeof ConnectionType];

export interface ConnectionStatus {
  type: ConnectionType;
  name: string;
  host: string | null;
  isConnected: boolean;
  error: string | null;
  checkedAt: string;
  latencyMs: number;
}

export interface ConnectionStatusSummary {
  connections: ConnectionStatus[];
  checkedAt: string;
}

/* ------------------------------------------------------------------ */
/*  Store                                                              */
/* ------------------------------------------------------------------ */

interface ConnectionStatusState {
  connections: ConnectionStatus[];
  checkedAt: string | null;
  isLoading: boolean;
  error: string | null;

  /** Interval id for polling (undefined = not polling) */
  _intervalId: ReturnType<typeof setInterval> | undefined;

  /** Fetch the latest connection status from the backend */
  fetchStatus: (token: string) => Promise<void>;

  /** Start periodic polling (default 30 000 ms) */
  startPolling: (getToken: () => Promise<string | null>, intervalMs?: number) => void;

  /** Stop periodic polling */
  stopPolling: () => void;
}

const API_URL = '/api/dotnet/connections/status';

export const useConnectionStatusStore = create<ConnectionStatusState>((set, get) => ({
  connections: [],
  checkedAt: null,
  isLoading: false,
  error: null,
  _intervalId: undefined,

  fetchStatus: async (token: string) => {
    set({ isLoading: true, error: null });
    try {
      const res = await fetch(API_URL, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        throw new Error(`Connection status request failed: ${res.status}`);
      }
      const data: ConnectionStatusSummary = await res.json();
      set({
        connections: data.connections,
        checkedAt: data.checkedAt,
        isLoading: false,
      });
    } catch (err) {
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  },

  startPolling: (getToken, intervalMs = 30_000) => {
    const { _intervalId, fetchStatus } = get();
    // Avoid double-starting
    if (_intervalId !== undefined) return;

    // Immediate first fetch
    void getToken().then((token) => {
      if (token) fetchStatus(token);
    });

    const id = setInterval(() => {
      void getToken().then((token) => {
        if (token) fetchStatus(token);
      });
    }, intervalMs);

    set({ _intervalId: id });
  },

  stopPolling: () => {
    const { _intervalId } = get();
    if (_intervalId !== undefined) {
      clearInterval(_intervalId);
      set({ _intervalId: undefined });
    }
  },
}));
